import { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';

const PLATFORM_OPTIONS = [
    { id: 'instagram', label: 'Instagram', icon: 'mdi:instagram', color: 'text-pink-600' },
    { id: 'facebook', label: 'Facebook', icon: 'mdi:facebook', color: 'text-blue-600' }
];

const POST_TYPES = [
    { value: 'feed', label: 'Feed' },
    { value: 'story', label: 'Story' },
    { value: 'reel', label: 'Reel' },
    { value: 'carousel', label: 'Carousel' }
];

const MAX_CHARS = 2200;

export default function MktPostDrawer({ open, onClose, onSave }) {
    const [platforms, setPlatforms] = useState([]);
    const [postType, setPostType] = useState('feed');
    const [content, setContent] = useState('');
    const [hashtags, setHashtags] = useState('');
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [saving, setSaving] = useState(false);

    const handleClose = () => {
        setPlatforms([]);
        setPostType('feed');
        setContent('');
        setHashtags('');
        setScheduleDate('');
        setScheduleTime('');
        onClose();
    };

    const togglePlatform = (platformId) => {
        setPlatforms((prev) =>
            prev.includes(platformId)
                ? prev.filter((p) => p !== platformId)
                : [...prev, platformId]
        );
    };

    const handleSave = async (asDraft = false) => {
        if (!platforms.length || !content.trim()) return;
        setSaving(true);
        try {
            await onSave({
                platforms,
                post_type: postType,
                content: content.trim(),
                hashtags: hashtags.trim(),
                scheduled_date: scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}:00` : null,
                status: asDraft ? 'draft' : 'scheduled'
            });
        } catch (err) {
            console.error('Erro ao salvar publicação:', err);
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className={d.drawer.overlay}>
            <div className={d.drawer.backdrop} onClick={handleClose} />
            <div className={d.drawer.panel}>
                {/* Header */}
                <div className={d.drawer.header}>
                    <div className="flex items-center gap-3">
                        <div className={d.drawer.headerIcon}>
                            <Iconify icon="eva:edit-2-outline" width={20} className="text-violet-600" />
                        </div>
                        <div>
                            <h2 className={d.drawer.headerTitle}>Nova Publicação</h2>
                            <p className={d.drawer.headerSubtitle}>Crie e agende sua publicação</p>
                        </div>
                    </div>
                    <button className={d.drawer.closeBtn} onClick={handleClose}>
                        <Iconify icon="eva:close-outline" width={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className={d.drawer.body}>
                    {/* Platforms */}
                    <div className={d.form.section}>
                        <h3 className={d.form.sectionTitle}>
                            <Iconify icon="eva:globe-2-outline" width={18} className="text-violet-600" />
                            Plataformas
                        </h3>
                        <div className="flex items-center gap-3">
                            {PLATFORM_OPTIONS.map((platform) => {
                                const isActive = platforms.includes(platform.id);
                                return (
                                    <label
                                        key={platform.id}
                                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-all ${
                                            isActive
                                                ? 'border-[#7C3AED] bg-violet-50 ring-2 ring-[#7C3AED]/20'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className={d.form.checkbox}
                                            checked={isActive}
                                            onChange={() => togglePlatform(platform.id)}
                                        />
                                        <Iconify icon={platform.icon} width={18} className={platform.color} />
                                        <span className="text-sm font-medium text-gray-700">{platform.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* Post Type */}
                    <div className={d.form.section}>
                        <h3 className={d.form.sectionTitle}>
                            <Iconify icon="eva:layers-outline" width={18} className="text-violet-600" />
                            Tipo de Publicação
                        </h3>
                        <label className={d.form.label}>Formato</label>
                        <select
                            className={d.form.input}
                            value={postType}
                            onChange={(e) => setPostType(e.target.value)}
                        >
                            {POST_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Content */}
                    <div className={d.form.section}>
                        <h3 className={d.form.sectionTitle}>
                            <Iconify icon="eva:file-text-outline" width={18} className="text-violet-600" />
                            Conteúdo
                        </h3>
                        <label className={d.form.label}>Texto da publicação</label>
                        <textarea
                            className={d.form.textarea}
                            placeholder="Escreva o conteúdo da sua publicação..."
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            maxLength={MAX_CHARS}
                        />
                        <div className="flex justify-end mt-1">
                            <span className={`text-xs ${content.length > MAX_CHARS * 0.9 ? 'text-amber-600' : 'text-gray-400'}`}>
                                {content.length}/{MAX_CHARS}
                            </span>
                        </div>

                        <label className={`${d.form.label} mt-4`}>Hashtags</label>
                        <input
                            type="text"
                            className={d.form.input}
                            placeholder="#marketing #socialmedia #lais"
                            value={hashtags}
                            onChange={(e) => setHashtags(e.target.value)}
                        />
                    </div>

                    {/* Media Upload */}
                    <div className={d.form.section}>
                        <h3 className={d.form.sectionTitle}>
                            <Iconify icon="eva:image-outline" width={18} className="text-violet-600" />
                            Mídia
                        </h3>
                        <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center hover:border-[#7C3AED]/40 transition-colors cursor-pointer">
                            <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center mb-3">
                                <Iconify icon="eva:cloud-upload-outline" width={24} className="text-gray-400" />
                            </div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Arraste ou clique para enviar</p>
                            <p className="text-xs text-gray-400">PNG, JPG, MP4 (máx. 50MB)</p>
                        </div>
                    </div>

                    {/* Schedule */}
                    <div className={d.form.section}>
                        <h3 className={d.form.sectionTitle}>
                            <Iconify icon="eva:clock-outline" width={18} className="text-violet-600" />
                            Agendamento
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={d.form.label}>Data</label>
                                <input
                                    type="date"
                                    className={d.form.input}
                                    value={scheduleDate}
                                    onChange={(e) => setScheduleDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={d.form.label}>Horário</label>
                                <input
                                    type="time"
                                    className={d.form.input}
                                    value={scheduleTime}
                                    onChange={(e) => setScheduleTime(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={d.drawer.footer}>
                    <button className={d.drawer.cancelBtn} onClick={handleClose}>
                        Cancelar
                    </button>
                    <button
                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors"
                        onClick={() => handleSave(true)}
                        disabled={!platforms.length || !content.trim() || saving}
                    >
                        Salvar Rascunho
                    </button>
                    <button
                        className={`${d.drawer.saveBtn} ${(!platforms.length || !content.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => handleSave(false)}
                        disabled={!platforms.length || !content.trim() || saving}
                    >
                        {saving ? (
                            <>
                                <Iconify icon="eva:loader-outline" width={16} className="animate-spin inline mr-1" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <Iconify icon="eva:calendar-outline" width={16} className="inline mr-1" />
                                Agendar
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
